import {
  buildEmailShell,
  buildWhenWhereCardHtml,
  escapeHtml,
  BODY_FONT,
  STORE_ICON_APPLE_URL,
  STORE_ICON_GOOGLE_PLAY_URL,
} from './emailShell';
import { formatEventDateRange } from './formatDateRange';
import { APP_STORE_URL, PLAY_STORE_URL } from '../constants/storeLinks';

/**
 * Primary email CTA. `href` is an https open link (see
 * `constants/openLinks.ts`): the landing page behind it opens the app when
 * installed and offers the stores otherwise, so one button serves phones,
 * desktops, and mail clients that rewrite links.
 */
export function buildOpenButtonHtml(
  href: string,
  label: string,
  caption?: string,
): string {
  const captionHtml = caption
    ? `
              <p style="margin:0 0 28px;font-size:14px;color:#888;font-family:${BODY_FONT};text-align:center;">${escapeHtml(caption)}</p>`
    : '';
  return `
              <div style="text-align:center;margin:0 0 ${caption ? 12 : 28}px;">
                <a href="${escapeHtml(href)}" style="display:inline-block;min-width:220px;background:#21376C;color:#ffffff;font-family:${BODY_FONT};font-size:15px;font-weight:600;text-decoration:none;padding:14px 32px;border-radius:6px;text-align:center;"><span style="color:#ffffff;">${escapeHtml(label)}</span></a>
              </div>${captionHtml}`;
}

/**
 * First name for the salutation. Display names arrive as "First Last"; a
 * single token is used as-is.
 */
function salutationName(recipientName: string | null): string | null {
  const trimmed = recipientName?.trim();
  if (!trimmed) return null;
  return trimmed.split(/\s+/)[0] ?? trimmed;
}

/**
 * Greeting + invitation line shared by the invitation and event-added
 * emails: "Patrick," then "You've been invited! Please join us for the
 * upcoming Flat Rock Global event: <event>."
 */
function buildInviteIntroHtml(recipientName: string | null, eventName: string): string {
  const first = salutationName(recipientName);
  const greeting = first ? `${escapeHtml(first)},` : 'Hello,';
  return `
              <p style="margin:0 0 20px;font-size:16px;color:#333;font-family:${BODY_FONT};">${greeting}</p>
              <p style="margin:0 0 28px;font-size:16px;color:#333;font-family:${BODY_FONT};line-height:1.6;">You've been invited! Please join us for the upcoming Flat Rock Global event: <strong>${escapeHtml(eventName)}</strong>.</p>`;
}

/**
 * Stacked, centered store buttons. Two rows rather than side-by-side so the
 * pair never overflows a phone-width email client. Plain `https://` links —
 * they work in every mail client, unlike custom-scheme deep links.
 */
export function buildStoreLinksHtml(): string {
  const button = (href: string, iconUrl: string, label: string) =>
    `<a href="${href}" style="display:inline-block;min-width:220px;background:#21376C;color:#ffffff;font-family:${BODY_FONT};font-size:14px;font-weight:600;text-decoration:none;padding:12px 24px;border-radius:6px;text-align:center;line-height:18px;"><img src="${iconUrl}" width="18" height="18" alt="" style="display:inline-block;vertical-align:middle;margin:-2px 10px 0 0;border:0;"><span style="color:#ffffff;">${label}</span></a>`;
  return `
              <div style="text-align:center;margin:0 0 10px;">${button(APP_STORE_URL, STORE_ICON_APPLE_URL, 'Download on the App Store')}</div>
              <div style="text-align:center;margin:0 0 28px;">${button(PLAY_STORE_URL, STORE_ICON_GOOGLE_PLAY_URL, 'Get it on Google Play')}</div>`;
}

/**
 * Invitation email — sent to people who do not yet have an app account.
 * They go through the contact → pending invitation → code-claim flow.
 *
 * Existing authenticated users do NOT receive this email; they receive
 * `buildEventAddedEmailHtml` instead (no code, deep-link CTA). The
 * `inviteToEvent` callable branches between the two by identity state.
 */
export interface InvitationEmailData {
  recipientName: string | null;
  /**
   * The address the invitation is registered under. Signing in with this
   * address claims the invitation automatically; any other address triggers
   * the reservation-code prompt. The email spells this out.
   */
  recipientEmail: string;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  eventLocation: string | null;
  invitationCode: string;
  /**
   * https download link for the event (buildEventDownloadLink). Readers of
   * this email have no account and almost never the app, so the button
   * sends them to their store; the landing page shows a QR code on a
   * computer. The app does not claim `/download/*`.
   */
  eventDownloadLink: string;
}

export function buildInvitationEmailSubject(eventName: string): string {
  return `You're Invited: ${eventName}`;
}

export function buildInvitationEmailHtml(data: InvitationEmailData): string {
  const {
    recipientName,
    recipientEmail,
    eventName,
    eventStartDate,
    eventEndDate,
    eventLocation,
    invitationCode,
    eventDownloadLink,
  } = data;

  const introHtml = buildInviteIntroHtml(recipientName, eventName);
  const dateRange = formatEventDateRange(eventStartDate, eventEndDate);
  const eventDetailsCard = buildWhenWhereCardHtml(dateRange, eventLocation);

  // Split 8-char codes into two 4-char chunks for readability ("KKKKHC87" →
  // "KKKK HC87"). For non-8-char codes, leave as-is — the format gracefully
  // handles whatever shape comes in.
  const formattedCode =
    invitationCode.length === 8
      ? `${invitationCode.slice(0, 4)} ${invitationCode.slice(4)}`
      : invitationCode;

  const codeBlock = `
              <p style="margin:0 0 6px;font-size:14px;color:#888;text-align:center;font-family:${BODY_FONT};letter-spacing:0.08em;text-transform:uppercase;">Reservation code</p>
              <div style="margin:0 0 28px;text-align:center;">
                <span style="display:inline-block;font-family:'Courier New',Courier,monospace;font-size:22px;font-weight:500;letter-spacing:2px;color:#21376C;padding:4px 0;border-bottom:1px solid #21376C;">${escapeHtml(formattedCode)}</span>
              </div>`;

  const bodyHtml = `${introHtml}

              ${eventDetailsCard}

              <p style="margin:0 0 20px;font-size:15px;color:#333;line-height:1.7;font-family:${BODY_FONT};">
                Your event details await in the Flat Rock Global Events app. Download it on your phone to get started.
              </p>

              ${buildOpenButtonHtml(eventDownloadLink, 'Download the app', 'Takes you to the App Store or Google Play.')}

              <div style="background:#f7f7fa;border-left:3px solid #21376C;padding:16px 20px;margin:0 0 24px;">
                <p style="margin:0 0 10px;color:#21376C;font-family:${BODY_FONT};font-size:14px;font-weight:600;letter-spacing:0.04em;text-transform:uppercase;">Signing in</p>
                <p style="margin:0 0 12px;color:#333;font-family:${BODY_FONT};font-size:14px;line-height:1.7;">
                  <strong>First time in the app?</strong> Sign in with this email address: <strong>${escapeHtml(recipientEmail)}</strong>. Your invitation is linked to it, so you will not be asked for a code.
                </p>
                <p style="margin:0;color:#333;font-family:${BODY_FONT};font-size:14px;line-height:1.7;">
                  If you need to use a different email, for example a personal address tied to your phone's Face ID or Google account, then you will be prompted for a reservation code. Provide the following code when requested.
                </p>
              </div>

              ${codeBlock}

              <p style="margin:0;font-size:14px;color:#888;font-family:${BODY_FONT};font-style:italic;">This code is tied to your invitation. Please keep it confidential.</p>`;

  return buildEmailShell({ eventName, bodyHtml });
}

// ── Event-added notification (no invitation code) ──

/**
 * Event-added email — sent to pre-existing authenticated users who are
 * being added to a new event. Auto-claimed at invite time, so no code
 * required. Deep-link CTA lands them in the app on the new event.
 */
export interface EventAddedEmailData {
  recipientName: string | null;
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  eventLocation: string | null;
  /**
   * https open link for the event (buildEventOpenLink). Recipients of this
   * email already have an account, so the CTA lands them in the app on the
   * new event; the landing page covers the not-installed case.
   */
  appDeepLink?: string | null;
}

export function buildEventAddedEmailSubject(eventName: string): string {
  return `You've Been Added to ${eventName}`;
}

export function buildEventAddedEmailHtml(data: EventAddedEmailData): string {
  const { recipientName, eventName, eventStartDate, eventEndDate, eventLocation, appDeepLink } = data;

  const introHtml = buildInviteIntroHtml(recipientName, eventName);
  const dateRange = formatEventDateRange(eventStartDate, eventEndDate);
  const eventDetailsCard = buildWhenWhereCardHtml(dateRange, eventLocation);

  const ctaButton = appDeepLink ? buildOpenButtonHtml(appDeepLink, 'Open in the App') : '';

  const closingLine = appDeepLink
    ? 'Tap above to view the event details such as your agenda, locations, FAQs, attendees, and more.'
    : 'Open the Flat Rock Global Events app and sign in to view the event details such as your agenda, locations, FAQs, attendees, and more.';

  const bodyHtml = `${introHtml}

              ${eventDetailsCard}

              ${ctaButton}

              <p style="margin:0;font-size:15px;color:#333;line-height:1.7;font-family:${BODY_FONT};">${closingLine}</p>`;

  return buildEmailShell({ eventName, bodyHtml });
}
